import { useParams } from "react-router-dom";
import Loginheader from "../components/loginheader/Loginheader";
import axios from "axios";
import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { Spin, Card, Tag, Alert, Button, Input, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, SwapOutlined, DownloadOutlined } from "@ant-design/icons";

export default function EmpTransferResponse() {
    const { token } = useParams();
    const { backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [respondedStatus, setRespondedStatus] = useState(null);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${backUrl}/api/emp/view-transfer-response/${token}/`);
                setDetails(res.data);
                if (res.data.employee_acceptance_status && res.data.employee_acceptance_status !== "PENDING") {
                    setRespondedStatus(res.data.employee_acceptance_status);
                }
            } catch (err) {
                setErrorMsg(err.response?.data?.error || "Invalid or expired transfer request link.");
            } finally {
                setLoading(false);
            }
        };
        if (token) fetchDetails();
    }, [token, backUrl]);

    const handleAction = async (actionType) => {
        setSubmitting(true);
        try {
            const res = await axios.put(`${backUrl}/api/emp/transfer-response/${token}/`, {
                action: actionType,
                remarks: remarks,
            });
            const newStatus = actionType === 'accept' ? 'ACCEPTED' : 'DECLINED';
            setRespondedStatus(newStatus);
            message.success(res.data.message || `Transfer request ${newStatus.toLowerCase()} successfully.`);
        } catch (err) {
            message.error(err.response?.data?.error || err.response?.data?.message || "Failed to submit decision. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ minHeight: "100vh", backgroundColor: "#f4f7fb", display: "flex", flexDirection: "column" }}>
            <Loginheader />
            <div style={{ maxWidth: 800, margin: "40px auto", padding: "0 16px", width: "100%", flexGrow: 1 }}>
                <Card
                    style={{ borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
                    title={
                        <div style={{ textAlign: "center", padding: "10px 0" }}>
                            <SwapOutlined style={{ fontSize: 28, color: "#0e497b", marginBottom: 8 }} />
                            <h2 style={{ margin: 0, color: "#0e497b", fontSize: 22, fontWeight: 700 }}>
                                Employee Transfer Order Confirmation
                            </h2>
                        </div>
                    }
                >
                    {loading ? (
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            <Spin size="large" tip="Loading transfer details..." />
                        </div>
                    ) : errorMsg ? (
                        <Alert type="error" message="Invalid Request" description={errorMsg} showIcon style={{ margin: "20px 0" }} />
                    ) : details ? (
                        <div>
                            <p style={{ color: "#555", fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                                Dear <strong>{details.employee_name}</strong> (Code: {details.current_employee_code || "N/A"}),
                                <br />
                                Please review your official Transfer Order details below and confirm your response.
                            </p>

                            {respondedStatus && (
                                <Alert
                                    type={respondedStatus === "ACCEPTED" ? "success" : "error"}
                                    showIcon
                                    message={
                                        respondedStatus === "ACCEPTED"
                                            ? "Transfer Request Accepted"
                                            : "Transfer Request Declined"
                                    }
                                    description={`You have already ${respondedStatus.toLowerCase()} this transfer order.`}
                                    style={{ marginBottom: 20 }}
                                />
                            )}

                            {/* Details Table */}
                            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, marginBottom: 24 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#0e497b", marginBottom: 12, textTransform: "uppercase" }}>
                                    Transfer Summary
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", fontSize: 13 }}>
                                    <div>
                                        <span style={{ color: "#64748b" }}>Current Organisation:</span>
                                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{details.current_organisation_name || "-"}</div>
                                    </div>
                                    <div>
                                        <span style={{ color: "#64748b" }}>New Organisation:</span>
                                        <div style={{ fontWeight: 600, color: "#0e497b" }}>{details.new_organisation_name || "-"}</div>
                                    </div>

                                    <div>
                                        <span style={{ color: "#64748b" }}>Current Department:</span>
                                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{details.current_department_name || "-"}</div>
                                    </div>
                                    <div>
                                        <span style={{ color: "#64748b" }}>New Department:</span>
                                        <div style={{ fontWeight: 600, color: "#0e497b" }}>{details.new_department_name || "-"}</div>
                                    </div>

                                    <div>
                                        <span style={{ color: "#64748b" }}>Current Designation:</span>
                                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{details.current_designation_name || "-"}</div>
                                    </div>
                                    <div>
                                        <span style={{ color: "#64748b" }}>New Designation:</span>
                                        <div style={{ fontWeight: 600, color: "#0e497b" }}>{details.new_designation_name || "-"}</div>
                                    </div>

                                    <div>
                                        <span style={{ color: "#64748b" }}>Current Location:</span>
                                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{details.current_location || "-"}</div>
                                    </div>
                                    <div>
                                        <span style={{ color: "#64748b" }}>New Work Location:</span>
                                        <div style={{ fontWeight: 600, color: "#0e497b" }}>{details.new_location || "-"}</div>
                                    </div>

                                    {details.effective_date_of_transfer && (
                                        <div style={{ gridColumn: "1 / -1" }}>
                                            <span style={{ color: "#64748b" }}>Effective Date of Transfer:</span>
                                            <div style={{ fontWeight: 700, color: "#10b981" }}>{details.effective_date_of_transfer}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {!respondedStatus && (
                                <>
                                    <div style={{ marginBottom: 20 }}>
                                        <label style={{ fontSize: 13, fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>
                                            Remarks / Comments (Optional):
                                        </label>
                                        <Input.TextArea
                                            rows={3}
                                            placeholder="Enter any comments regarding your transfer decision..."
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                        />
                                    </div>

                                    <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 24 }}>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<CheckCircleOutlined />}
                                            loading={submitting}
                                            onClick={() => handleAction("accept")}
                                            style={{ backgroundColor: "#10b981", borderColor: "#10b981", minWidth: 160, borderRadius: 8 }}
                                        >
                                            Accept Transfer
                                        </Button>

                                        <Button
                                            danger
                                            type="primary"
                                            size="large"
                                            icon={<CloseCircleOutlined />}
                                            loading={submitting}
                                            onClick={() => handleAction("decline")}
                                            style={{ minWidth: 160, borderRadius: 8 }}
                                        >
                                            Decline Transfer
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : null}
                </Card>
            </div>
        </div>
    );
}
